import moment from 'moment';

/**
 * Named relative date ranges, shared by the export page and the
 * scheduled-export runner.
 *
 * The keys and their semantics deliberately MIRROR the reporting
 * `AdvancedDateRangeSelector` the dashboard uses (`DateRangePreset` in
 * `features/reporting/components/advanced-date-range-selector.tsx`): the
 * export page renders that same selector, so a second vocabulary would mean
 * translating between them on every save and drifting apart on the first
 * change to either. Keep the two lists in step.
 *
 * A saved or scheduled export cannot store two fixed dates — "Last Month" has
 * to mean last month on every run, not the month it happened to be when the
 * schedule was created — so a schedule stores the KEY and resolves it at run
 * time. `custom` is the escape hatch that carries explicit dates instead, and
 * is also what the selector's back/forward arrows produce once they step past
 * a named period.
 */
export type DateRangePresetKey =
  | 'today'
  | 'thisWeek'
  | 'thisMonth'
  | 'thisQuarter'
  | 'thisYear'
  | 'yesterday'
  | 'lastWeek'
  | 'lastMonth'
  | 'lastQuarter'
  | 'lastYear'
  | 'tomorrow'
  | 'nextWeek'
  | 'nextTwoWeeks'
  | 'last7Days'
  | 'last30Days'
  | 'next7Days'
  | 'next30Days'
  | 'nextMonth'
  | 'nextQuarter'
  | 'nextYear'
  | 'allTime'
  | 'custom';

export interface ResolvedDateRange {
  /** Inclusive start, `YYYY-MM-DD`. */
  start: string;
  /** Inclusive end, `YYYY-MM-DD`. */
  end: string;
}

export const DATE_RANGE_PRESET_LABELS: Record<DateRangePresetKey, string> = {
  today: 'Today',
  thisWeek: 'This Week',
  thisMonth: 'This Month',
  thisQuarter: 'This Quarter',
  thisYear: 'This Year',
  yesterday: 'Yesterday',
  lastWeek: 'Last Week',
  lastMonth: 'Last Month',
  lastQuarter: 'Last Quarter',
  lastYear: 'Last Year',
  tomorrow: 'Tomorrow',
  nextWeek: 'Next Week',
  nextTwoWeeks: 'Next 2 Weeks',
  last7Days: 'Last 7 Days',
  last30Days: 'Last 30 Days',
  next7Days: 'Next 7 Days',
  next30Days: 'Next 30 Days',
  nextMonth: 'Next Month',
  nextQuarter: 'Next Quarter',
  nextYear: 'Next Year',
  allTime: 'All Time',
  custom: 'Custom Range',
};

/** Every preset key, in the selector's own order. */
export const DATE_RANGE_PRESET_KEYS = Object.keys(
  DATE_RANGE_PRESET_LABELS,
) as DateRangePresetKey[];

/**
 * The floor for an `allTime` export.
 *
 * The selector resolves `allTime` to a one-year placeholder and leaves the
 * real range to the backend. An export has to put real dates on the query, so
 * it uses a fixed floor comfortably before any Farmshare record rather than
 * silently shipping twelve months under an "All Time" label.
 */
export const ALL_TIME_START = '2015-01-01';

const FORMAT = 'YYYY-MM-DD';

/**
 * Which calendar a preset is resolved against.
 *
 * - `utc` — the server's. The scheduled-export Lambda runs in UTC like every
 *   other cron in this repo, and vendors carry no timezone of their own.
 * - `local` — the caller's own clock. What a person at a screen means by
 *   "Today", and what the dashboard date selector resolves its presets in.
 */
export type DateRangePresetZone = 'utc' | 'local';

/**
 * Resolve a preset key into concrete inclusive dates.
 *
 * Calendar math in UTC by default: the export mutation reads its `startDate`
 * / `endDate` strings as UTC day boundaries
 * (`DateTime.fromISO(date).startOf('day')`), and the scheduled-export Lambda
 * runs in UTC. `now` is injectable so the scheduler and tests can pin it.
 *
 * A PAGE must pass `zone: 'local'` and resolve every named period through
 * here — on selection, on reload, and when a saved export is loaded. The date
 * selector resolves its presets on the local clock, so resolving the same key
 * in UTC on another path makes "Today" two different days on one screen for
 * the hours the two calendars disagree.
 * @param preset - The preset to resolve.
 * @param options - `now` pins the reference instant; `custom` supplies the
 *   explicit range used when `preset` is `custom`; `zone` picks the calendar.
 * @returns Inclusive `YYYY-MM-DD` start and end dates.
 */
export function resolveDateRangePreset(
  preset: DateRangePresetKey,
  options?: {
    now?: Date;
    custom?: Partial<ResolvedDateRange>;
    zone?: DateRangePresetZone;
  },
): ResolvedDateRange {
  const reference = options?.now ?? new Date();
  const now =
    options?.zone === 'local' ? moment(reference) : moment.utc(reference);

  /** `now` shifted by `amount` units, as that unit's full span. */
  const span = (
    amount: number,
    unit: 'day' | 'week' | 'month' | 'quarter' | 'year',
  ): ResolvedDateRange => {
    const anchor = now.clone().add(amount, unit);
    return {
      start: anchor.clone().startOf(unit).format(FORMAT),
      end: anchor.clone().endOf(unit).format(FORMAT),
    };
  };

  switch (preset) {
    case 'today':
      return span(0, 'day');
    case 'yesterday':
      return span(-1, 'day');
    case 'tomorrow':
      return span(1, 'day');
    case 'thisWeek':
      return span(0, 'week');
    case 'lastWeek':
      return span(-1, 'week');
    case 'nextWeek':
      return span(1, 'week');
    // Rolling days, today included.
    case 'last7Days':
    case 'last30Days': {
      const n = preset === 'last7Days' ? 7 : 30;
      return {
        start: now
          .clone()
          .subtract(n - 1, 'days')
          .startOf('day')
          .format(FORMAT),
        end: now.clone().endOf('day').format(FORMAT),
      };
    }
    case 'next7Days':
    case 'next30Days': {
      const n = preset === 'next7Days' ? 7 : 30;
      return {
        start: now.clone().startOf('day').format(FORMAT),
        end: now
          .clone()
          .add(n - 1, 'days')
          .endOf('day')
          .format(FORMAT),
      };
    }
    case 'nextTwoWeeks':
      // Today and the 13 days after it.
      return {
        start: now.clone().startOf('day').format(FORMAT),
        end: now.clone().add(13, 'days').endOf('day').format(FORMAT),
      };
    case 'thisMonth':
      return span(0, 'month');
    case 'lastMonth':
      return span(-1, 'month');
    case 'nextMonth':
      return span(1, 'month');
    case 'thisQuarter':
      return span(0, 'quarter');
    case 'lastQuarter':
      return span(-1, 'quarter');
    case 'nextQuarter':
      return span(1, 'quarter');
    case 'thisYear':
      return span(0, 'year');
    case 'lastYear':
      return span(-1, 'year');
    case 'nextYear':
      return span(1, 'year');
    case 'allTime':
      return { start: ALL_TIME_START, end: now.clone().format(FORMAT) };
    case 'custom':
    default:
      // A `custom` preset with no dates is a misconfiguration rather than an
      // error worth throwing from a cron — fall back to the current month so
      // the run still produces something legible.
      return {
        start:
          options?.custom?.start || now.clone().startOf('month').format(FORMAT),
        end: options?.custom?.end || now.clone().endOf('month').format(FORMAT),
      };
  }
}

/**
 * Whether a preset key is one this build understands. Guards values coming
 * back from the database, which may predate or postdate this code.
 * @param value - Candidate preset key.
 */
export function isDateRangePresetKey(
  value: unknown,
): value is DateRangePresetKey {
  return (
    typeof value === 'string' &&
    (DATE_RANGE_PRESET_KEYS as string[]).includes(value)
  );
}

/**
 * Whether a preset resolves relative to "now" — i.e. a schedule using it will
 * export a different window on each run. `custom` (fixed dates) is the only
 * one that does not.
 * @param preset - The preset to classify.
 */
export function isRelativeDateRangePreset(preset: DateRangePresetKey): boolean {
  return preset !== 'custom';
}
